import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { FaMapMarkerAlt, FaHome, FaArrowLeft } from 'react-icons/fa';
import { motion } from 'framer-motion';
import PropertyCard from '../components/properties/PropertyCard';
import { getSocietiesFromSheet } from '../services/localDataService';

const LocationPage = () => {
  const { area } = useParams();
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const decodedArea = decodeURIComponent(area).replace(/-/g, ' ');

  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const result = await getSocietiesFromSheet();
        if (result.success) {
          // Filter societies that include the area name in their location
          const filtered = result.data.filter(s => 
            s.location.toLowerCase().includes(decodedArea.toLowerCase())
          );
          setSocieties(filtered);
        } else {
          setError('Failed to load properties.');
        }
      } catch {
        setError('An error occurred.');
      } finally {
        setLoading(false);
      }
    };
    fetchSocieties();
  }, [decodedArea]);

  return (
    <main>
      <SEO 
        title={`Premium PG & Accommodations in ${decodedArea} | Chapter24`}
        description={`Find the best Girls PGs and Premium Stays in ${decodedArea}, Pune. Zero brokerage, 100% verified properties near IT hubs.`}
        url={`/location/${area}`}
      />

      <section className="bg-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-primary-800 z-0"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center"></div>
        
        <div className="container-custom relative z-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary-200 hover:text-white mb-6 text-sm font-medium transition">
            <FaArrowLeft /> Back Home
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-800/50 backdrop-blur-sm border border-primary-700/50 text-primary-200 text-sm font-bold tracking-widest uppercase mb-4">
            <FaMapMarkerAlt /> {decodedArea}, Pune
          </div>
          <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-4 leading-tight">
            Girls PG in <span className="text-primary-400">{decodedArea}</span>
          </h1>
          <p className="text-lg md:text-xl text-primary-100 max-w-2xl mx-auto">
            Discover safe, premium, and fully-furnished accommodations curated for working women in {decodedArea}.
          </p>
        </div>
      </section>

      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Properties in {decodedArea}</h2>
            <p className="text-gray-500 text-sm">
              {loading ? 'Loading…' : `${societies.length} propert${societies.length !== 1 ? 'ies' : 'y'} found`}
            </p>
            {error && <p className="text-red-600 mt-4">{error}</p>}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-md h-96 animate-pulse">
                  <div className="h-52 bg-gray-200 rounded-t-2xl" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-12 bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : societies.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
              <FaHome className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Properties Found in {decodedArea}</h3>
              <p className="text-gray-400 mb-6">We are expanding rapidly. Check back soon or view all our locations.</p>
              <Link to="/properties" className="btn-primary">View All Properties</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {societies.map((society, index) => (
                <motion.div
                  key={society.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                >
                  <PropertyCard property={society} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default LocationPage;
